import * as React from "react";
import { AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./alert-dialog";
import { cn } from "../lib/utils";

export interface ConfirmDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "default" | "destructive" | "warning";
  onConfirm?: () => void | Promise<void>;
  onCancel?: (event?: React.MouseEvent) => void;
  loading?: boolean;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  /**
   * Custom content to render between description and buttons.
   * This can be a function that returns React content or static React content.
   * The parent is fully responsible for managing confirmDisabled state.
   */
  customContent?: React.ReactNode | (() => React.ReactNode);
  /**
   * Whether the confirm button should be disabled.
   * The parent component is fully responsible for this state.
   */
  confirmDisabled?: boolean;
}

const variantConfig = {
  default: {
    iconColor: "text-blue-500",
    confirmVariant: "default" as const,
  },
  destructive: {
    iconColor: "text-red-500",
    confirmVariant: "destructive" as const,
  },
  warning: {
    iconColor: "text-amber-500",
    confirmVariant: "default" as const,
  },
};

export function ConfirmDialog({
  open,
  onOpenChange,
  title = "Are you sure?",
  description = "This action cannot be undone.",
  confirmText = "Continue",
  cancelText = "Cancel",
  variant = "default",
  onConfirm,
  onCancel,
  loading = false,
  icon,
  children,
  customContent,
  confirmDisabled = false,
}: ConfirmDialogProps) {
  const config = variantConfig[variant];
  const [isConfirming, setIsConfirming] = React.useState(false);

  const handleConfirm = React.useCallback(async () => {
    if (!onConfirm) return;

    setIsConfirming(true);
    try {
      await onConfirm();
      onOpenChange?.(false);
    } catch (error) {
      console.error("Error in confirm action:", error);
    } finally {
      setIsConfirming(false);
    }
  }, [onConfirm, onOpenChange]);

  const handleCancel = React.useCallback(
    (event?: React.MouseEvent) => {
      if (isConfirming) return; // Prevent cancel during confirmation
      onCancel?.(event);
      onOpenChange?.(false);
    },
    [onCancel, onOpenChange, isConfirming]
  );

  const isLoading = loading || isConfirming;
  const isButtonDisabled = isLoading || confirmDisabled;

  // Render custom content - fully controlled by parent
  const renderedCustomContent = React.useMemo(() => {
    if (typeof customContent === "function") {
      // For function-based content, we don't pass setConfirmDisabled anymore
      // The parent is responsible for managing the disabled state
      return customContent();
    }
    return customContent;
  }, [customContent]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className={cn("flex-shrink-0", config.iconColor)}>{icon || <AlertTriangle className="h-5 w-5" />}</div>
            <div className="flex-1 min-w-0">
              <AlertDialogTitle className="text-left">{title}</AlertDialogTitle>
            </div>
          </div>
          {description && <AlertDialogDescription className="text-left pl-8">{description}</AlertDialogDescription>}
        </AlertDialogHeader>

        {renderedCustomContent && <div className="px-6 pb-2">{renderedCustomContent}</div>}

        {children && <div className="pl-8 pb-2">{children}</div>}

        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel} disabled={isLoading}>
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isButtonDisabled}
            className={cn(
              config.confirmVariant === "destructive" &&
                "bg-destructive text-destructive-foreground hover:bg-destructive/90"
            )}
          >
            {isLoading ? "Please wait..." : confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
